import zodToJsonSchema from 'zod-to-json-schema';

import {
  ExternalArtifactTypesEnum,
  LlmOutputTypesEnum,
  PatientArtifactTypesEnum,
} from '@eluve/graphql-types';

import { differentialDiagnosisSchema } from './differential-diagnoses';
import { patientOverviewSchema } from './patient-overview.schema';
import { redFlagsOutputSchema } from './red-flags.schema';

const patientArtifactsDescriptions = {
  intake_form:
    'Patient intake form. This will only be used if no previous patient overview is available. This form is imported from EHR and does not have a consistent schema. Comes from external_patient_artifacts table.',
  lab_results:
    'Results from laboratory tests. This does not have a consistent schema at the moment. Comes from external_patient_artifacts table.',
  temp_medical_notes:
    'Any temporary medical notes. These are stored in external_patient_artifacts table.',
  all_medical_notes:
    'Signed collection of all medical notes for the patient. These are signed human output for the patient. The value gets populated dynamically during prompt building.',
  latest_medical_note:
    'Most recent signed medical note. The value gets populated dynamically during prompt building.',
  latest_medical_note_2:
    'Second most recent signed medical note (one prior to latest_medical_note). Unset when fewer than 2 prior signed notes exist. The value gets populated dynamically during prompt building.',
  latest_medical_note_3:
    'Third most recent signed medical note (two prior to latest_medical_note). Unset when fewer than 3 prior signed notes exist. The value gets populated dynamically during prompt building.',
  latest_doctor_note:
    "Most recent prior SOAP note authored by the current appointment's clinician, read from the external_charts cache (refreshed upstream by the fetchPatientCharts workflow step) and filtered via external_users.external_id. Permissive when the user-mapping table has no row for this clinician (ENG-4083 backfill) — returns all priors unfiltered with a warn log. Empty for non-EHR-integrated tenants.",
  latest_doctor_note_2:
    'Second most recent prior SOAP note authored by the current clinician. Same source + filter as latest_doctor_note. Unset when fewer than 2 doctor-scoped priors exist.',
  latest_doctor_note_3:
    'Third most recent prior SOAP note authored by the current clinician. Same source + filter as latest_doctor_note. Unset when fewer than 3 doctor-scoped priors exist.',
  all_doctor_notes:
    'All prior SOAP notes authored by the current clinician, read from the external_charts cache and filtered via external_users.external_id. Same caveats as latest_doctor_note.',
  patient_details:
    'Basic patient profile information. Includes sex and date of birth, as well as raw data imported from EHR. The data is available in external_patients_info table.',
  patient_overview:
    'Current patient overview. The value is obtained from patient_overviews table.',
  latest_appointment_intake_conversation:
    'Avatar appointment conversation from the most recent appointment.',
  current_appointment_date:
    'Date of the current appointment being documented, formatted as D-Month-YYYY (e.g. "28-April-2026"). Resolved in the appointment location\'s IANA timezone, falling back to UTC when no location timezone is set. Sourced from appointments.start_date.',
  previous_appointment_date:
    'Date of the most recent prior signed appointment for the same patient, formatted as D-Month-YYYY (e.g. "28-April-2026"). Aligned with the appointment that produced latest_medical_note, and resolved in *that* appointment\'s location timezone. An EHR chart with no matching Eluve appointment is rendered in the current appointment\'s timezone; UTC is the last resort. Unset when no prior signed note exists.',
  previous_appointment_date_2:
    'Date of the second most recent prior signed appointment for the same patient. Aligned with latest_medical_note_2. Same timezone resolution as previous_appointment_date. Unset when fewer than 2 prior signed notes exist.',
  previous_appointment_date_3:
    'Date of the third most recent prior signed appointment for the same patient. Aligned with latest_medical_note_3. Same timezone resolution as previous_appointment_date. Unset when fewer than 3 prior signed notes exist.',
  days_since_last_appointment:
    'Whole calendar days between the most recent prior signed appointment (the one behind latest_medical_note / previous_appointment_date) and the current appointment, rendered like "14 days" ("1 day", "0 days" for same-day). Each date is resolved in its own appointment location timezone, with the same fallbacks as previous_appointment_date. Unset when no prior signed note exists or the prior appointment is dated after the current one.',
  doctor_all_medical_notes:
    "Same as all_medical_notes, but only visits attributed to the current appointment's clinician. Attribution prefers the doctor who signed in the EHR and falls back to the owner of the Eluve appointment; visits attributable to neither are omitted. Use alongside all_medical_notes when a template should separate this clinician's own history from the rest.",
  doctor_latest_medical_note:
    "Same as latest_medical_note, but the most recent visit attributed to the current appointment's clinician. Unset when this clinician has no prior visit with the patient — which is common, and is not the same as the patient having no history.",
  doctor_latest_medical_note_2:
    'Second most recent prior note authored by the current clinician. Unset when fewer than 2 such visits exist.',
  doctor_latest_medical_note_3:
    'Third most recent prior note authored by the current clinician. Unset when fewer than 3 such visits exist.',
  doctor_previous_appointment_date:
    "Date of the most recent prior visit attributed to the current clinician, formatted as D-Month-YYYY. Aligned with doctor_latest_medical_note. Same timezone resolution as previous_appointment_date. This is 'when I last saw this patient', not 'when the patient was last seen'.",
  doctor_previous_appointment_date_2:
    'Date of the second most recent prior visit attributed to the current clinician. Aligned with doctor_latest_medical_note_2. Unset when fewer than 2 such visits exist.',
  doctor_previous_appointment_date_3:
    'Date of the third most recent prior visit attributed to the current clinician. Aligned with doctor_latest_medical_note_3. Unset when fewer than 3 such visits exist.',
  doctor_days_since_last_appointment:
    'Whole calendar days since the current clinician last saw this patient, rendered like "14 days". Counts only that clinician\'s own visits, so it is often larger than days_since_last_appointment. Unset when this clinician has no prior visit with the patient.',
  language:
    'Output language code for the current appointment (e.g. "en", "id"). Sourced from appointments.output_language.',
} as const satisfies Record<
  Lowercase<
    | Exclude<ExternalArtifactTypesEnum, 'HISTORICAL_CHART'>
    | PatientArtifactTypesEnum
  >,
  string
>;

const userSettingsDescriptions = {
  custom_instructions:
    'Currently, custom instructions are already appended to both the prompt and the output template (top-level description). Using this variable should not be necessary, except for conducting additional experiments.',
  user_specialty:
    'The professional specialty or department of the user, such as "Cardiology". This can be set in User Settings.',
};

const patientOverviewDescriptions: Record<string, string> = {
  ...patientArtifactsDescriptions,
  schema: JSON.stringify(zodToJsonSchema(patientOverviewSchema), undefined, 2),
};

const redFlagsDescriptions = {
  ...patientArtifactsDescriptions,
  schema: JSON.stringify(zodToJsonSchema(redFlagsOutputSchema), undefined, 2),
};

const diagnosesJustificationDescription = {
  ...patientArtifactsDescriptions,
  schema: JSON.stringify(
    zodToJsonSchema(differentialDiagnosisSchema),
    undefined,
    2,
  ),
  target_diagnosis:
    'The diagnosis that the indicators are being generated for.',
};

export const LLM_OUTPUT_TYPE_VARIABLES: Record<LlmOutputTypesEnum, string[]> = {
  SOAP_NOTE: [
    'transcript',
    'schema',
    ...Object.keys(userSettingsDescriptions),
    ...Object.keys(patientArtifactsDescriptions),
  ],
  FACT_VERIFICATION: ['summary', 'statement'],
  CLIENT_RECAP: ['transcript'],
  // Must mirror the variables passed in generateBillingCodesFromHumanOutput
  // (apps/api-nest llm-task-handler.service.ts) — no automated link
  BILLING_CODE_RECOMMENDATION: [
    'transcript',
    'summary',
    'schema',
    'user_specialty',
    'appointment_duration',
    'location_name',
    'patient_age',
  ],
  CHAT: [],
  DIFFERENTIAL_DIAGNOSES: ['transcript', 'schema'],
  GENERIC_OUTPUT: [],
  REVIEW_OF_SYSTEMS: ['transcript', 'schema'],
  PFSH: ['transcript', 'schema'],
  FACT_GENERATION: ['artifactContent', 'llmNote', 'humanEditedNote', 'schema'],
  CHIEF_COMPLAINT: ['transcript', 'schema'],
  PII_REMOVAL: ['text', 'schema'],
  DYNAMIC_OUTPUT: [
    'transcript',
    'schema',
    ...Object.keys(userSettingsDescriptions),
    ...Object.keys(patientArtifactsDescriptions),
  ],
  TRANSLATION: ['items', 'inputLanguage', 'outputLanguage'],
  MEDICINE_RECOMMENDATION: ['transcript', 'schema'],
  PATIENT_OVERVIEW: Object.keys(patientOverviewDescriptions),
  DIFFERENTIAL_DIAGNOSES_PLUS_RED_FLAGS: [
    'transcript',
    'medical_context',
    'patient_details',
  ],
  DIAGNOSES_JUSTIFICATION: [
    'transcript',
    ...Object.keys(diagnosesJustificationDescription),
  ],
  //This is a special case where we don't want to include any variables
  TRANSCRIPTION: ['languageCode'],
  PREVISIT_INSIGHTS: [],
  // Must mirror buildTasksExtractionVariables in
  // libs/backend/mini-apps clinical-tasks-extraction.core.ts
  TASK_EXTRACTION: [
    'transcript',
    'chief_complaint',
    'custom_instructions_section',
    'already_captured_section',
  ],
};

export const LLM_OUTPUT_TYPE_DESCRIPTIONS: Partial<
  Record<LlmOutputTypesEnum, Record<string, string>>
> = {
  PATIENT_OVERVIEW: patientOverviewDescriptions,
  SOAP_NOTE: {
    ...patientArtifactsDescriptions,
    ...userSettingsDescriptions,
  },
  DYNAMIC_OUTPUT: {
    ...patientArtifactsDescriptions,
    ...userSettingsDescriptions,
  },
  DIFFERENTIAL_DIAGNOSES_PLUS_RED_FLAGS: redFlagsDescriptions,
  DIAGNOSES_JUSTIFICATION: diagnosesJustificationDescription,
};

export const isLlmOutputSchedulable = (
  outputName: string | LlmOutputTypesEnum,
) => {
  // Outputs produced by their own pipeline rather than the post-note
  // summarization scheduler. Two consequences, both wanted for task
  // extraction: the scheduler never queues an LLM job for them, and the
  // tenant/user prompt-defaults pickers show them without a
  // `tenant_llm_outputs` row (which only exists for scheduled outputs).
  const nonSchedulableTypes: LlmOutputTypesEnum[] = [
    'TRANSLATION',
    'CHAT',
    'PATIENT_OVERVIEW',
    'DIFFERENTIAL_DIAGNOSES_PLUS_RED_FLAGS',
    'DIAGNOSES_JUSTIFICATION',
    'PREVISIT_INSIGHTS',
    'TASK_EXTRACTION',
  ];

  const outputType =
    typeof outputName === 'string'
      ? (outputName as LlmOutputTypesEnum)
      : outputName;

  return !nonSchedulableTypes.includes(outputType);
};
